import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { http, formatApiError, API } from "../lib/api";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const CATEGORIES = ["Residential", "Commercial", "Interior"];

const money = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function MarketingAdminPage() {
  const [tab, setTab] = useState("hero");
  return (
    <Layout>
      <div className="max-w-5xl">
        <div className="label-overline text-orange-600">Public Site CMS</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter uppercase mt-1">
          Marketing Admin
        </h1>
        <p className="text-sm text-zinc-600 mt-2">Manage the public website — hero image, projects gallery, properties for sale, and builder inquiries.</p>

        <div className="mt-8 flex flex-wrap gap-2" data-testid="cms-tabs">
          {["hero", "login-slider", "landing", "builders", "team", "projects", "properties", "blog", "inquiries"].map((t) => (
            <button key={t} data-testid={`cms-tab-${t}`}
                    onClick={() => setTab(t)}
                    className={`label-overline px-3 py-1.5 border ${tab === t ? "bg-black text-white border-black" : "border-zinc-400 hover:bg-zinc-100"}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "hero" && <HeroAdmin />}
          {tab === "login-slider" && <LoginSliderAdmin />}
          {tab === "landing" && <LandingAdmin />}
          {tab === "builders" && <BuildersAdmin />}
          {tab === "team" && <TeamAdmin />}
          {tab === "projects" && <ProjectsAdmin />}
          {tab === "properties" && <PropertiesAdmin />}
          {tab === "blog" && <BlogAdmin />}
          {tab === "inquiries" && <InquiriesAdmin />}
        </div>
      </div>
    </Layout>
  );
}

// ---------- HERO ----------
function HeroAdmin() {
  const [hero, setHero] = useState({ image_url: "", headline: "", subheadline: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    http.get("/marketing/site").then(r => setHero(r.data.hero || {})).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("headline", hero.headline || "");
      fd.append("subheadline", hero.subheadline || "");
      if (file) fd.append("file", file);
      const { data } = await http.post("/marketing/hero", fd);
      setHero(data);
      setFile(null);
      toast.success("Hero saved");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="hero-admin-form">
      {hero.image_url && (
        <img src={hero.image_url.startsWith("/") ? `${API}${hero.image_url.replace(/^\/api/, "")}` : hero.image_url}
             alt="" className="w-full max-w-2xl aspect-[16/9] object-cover border border-zinc-300" />
      )}
      <div>
        <label className="label-overline text-zinc-600 block mb-1">Hero Image</label>
        <input type="file" accept="image/*" data-testid="hero-file"
               onChange={(e) => setFile(e.target.files?.[0] || null)}
               className="text-sm" />
      </div>
      <div>
        <label className="label-overline text-zinc-600 block mb-1">Headline</label>
        <Input value={hero.headline || ""} onChange={(e) => setHero({...hero, headline: e.target.value})}
               data-testid="hero-headline" className="rounded-none border-zinc-400" />
      </div>
      <div>
        <label className="label-overline text-zinc-600 block mb-1">Sub-headline</label>
        <Input value={hero.subheadline || ""} onChange={(e) => setHero({...hero, subheadline: e.target.value})}
               data-testid="hero-subheadline" className="rounded-none border-zinc-400" />
      </div>
      <Button type="submit" disabled={saving} data-testid="hero-save"
              className="rounded-none bg-blue-600 hover:bg-blue-700 text-white font-mono uppercase tracking-widest text-xs">
        {saving ? "Saving…" : "Save Hero"}
      </Button>
    </form>
  );
}

// ---------- PROJECTS ----------
function ProjectsAdmin() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ title: "", category: "Residential", location: "", description: "", featured: false });
  const [busy, setBusy] = useState(false);

  const load = () => http.get("/marketing/projects").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/marketing/projects", form);
      setForm({ title: "", category: "Residential", location: "", description: "", featured: false });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try { await http.delete(`/marketing/projects/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadPhoto = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await http.post(`/marketing/projects/${id}/photos`, fd); load(); toast.success("Photo added"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8">
      <form onSubmit={create} className="border border-zinc-300 p-4 space-y-3" data-testid="new-project-form">
        <div className="label-overline text-zinc-600">Add Project</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Input required placeholder="Title *" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
                 data-testid="proj-title" className="rounded-none border-zinc-400" />
          <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
                  data-testid="proj-cat" className="h-10 px-3 border border-zinc-400 rounded-none text-sm">
            {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <Input placeholder="Location" value={form.location} onChange={(e) => setForm({...form, location: e.target.value})}
                 data-testid="proj-loc" className="rounded-none border-zinc-400" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.featured} onChange={(e) => setForm({...form, featured: e.target.checked})}
                   data-testid="proj-featured" /> Featured
          </label>
        </div>
        <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                  data-testid="proj-desc" rows={2} className="w-full border border-zinc-400 rounded-none p-2 text-sm" />
        <Button type="submit" disabled={busy} data-testid="proj-create"
                className="rounded-none bg-blue-600 hover:bg-blue-700 text-white font-mono uppercase tracking-widest text-xs">
          Add Project
        </Button>
      </form>

      <div className="space-y-4">
        {items.length === 0 && <div className="text-sm text-zinc-500">No projects yet.</div>}
        {items.map((p) => (
          <ItemRow key={p.id} item={p}
                   testIdPrefix={`proj-${p.id}`}
                   subtitle={`${p.category} · ${p.location || "—"}`}
                   editFields={
                     <ProjectEdit item={p} onSaved={load} />
                   }
                   onDelete={() => remove(p.id)}
                   onUpload={(f) => uploadPhoto(p.id, f)} />
        ))}
      </div>
    </div>
  );
}

// ---------- PROPERTIES ----------
function PropertiesAdmin() {
  const [items, setItems] = useState([]);
  const EMPTY = { title: "", price: 0, location: "", area_sqft: 0, description: "", contact: "",
                  amenities: [], latitude: null, longitude: null, status: "available" };
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const AMENITIES = ["Parking", "Garden", "Lift", "Power backup", "Security", "Swimming pool", "Gym", "Modular kitchen"];

  const load = () => http.get("/marketing/properties").then(r => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleAmenity = (a) => setForm((f) =>
    f.amenities.includes(a) ? { ...f, amenities: f.amenities.filter((x) => x !== a) } : { ...f, amenities: [...f.amenities, a] }
  );

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/marketing/properties", {
        ...form,
        price: Number(form.price) || 0,
        area_sqft: Number(form.area_sqft) || 0,
        latitude: form.latitude === "" || form.latitude == null ? null : Number(form.latitude),
        longitude: form.longitude === "" || form.longitude == null ? null : Number(form.longitude),
      });
      setForm(EMPTY);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this property?")) return;
    try { await http.delete(`/marketing/properties/${id}`); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const uploadPhoto = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await http.post(`/marketing/properties/${id}/photos`, fd); load(); toast.success("Photo added"); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8">
      <form onSubmit={create} className="border border-zinc-300 p-4 space-y-3" data-testid="new-property-form">
        <div className="label-overline text-zinc-600">Add Property</div>
        <div className="grid md:grid-cols-2 gap-3">
          <Input required placeholder="Title *" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
                 data-testid="prop-title" className="rounded-none border-zinc-400" />
          <Input type="number" placeholder="Price (₹)" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})}
                 data-testid="prop-price" className="rounded-none border-zinc-400" />
          <Input placeholder="Location" value={form.location} onChange={(e) => setForm({...form, location: e.target.value})}
                 data-testid="prop-loc" className="rounded-none border-zinc-400" />
          <Input type="number" placeholder="Area (sq.ft)" value={form.area_sqft} onChange={(e) => setForm({...form, area_sqft: e.target.value})}
                 data-testid="prop-area" className="rounded-none border-zinc-400" />
          <Input placeholder="Contact (phone / email)" value={form.contact} onChange={(e) => setForm({...form, contact: e.target.value})}
                 data-testid="prop-contact" className="rounded-none border-zinc-400" />
          <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}
                  data-testid="prop-status" className="h-10 px-3 border border-zinc-400 rounded-none text-sm">
            <option value="available">Available</option>
            <option value="hold">On hold</option>
            <option value="sold">Sold</option>
          </select>
          <Input type="number" step="any" placeholder="Latitude" value={form.latitude ?? ""} onChange={(e) => setForm({...form, latitude: e.target.value})}
                 data-testid="prop-lat" className="rounded-none border-zinc-400" />
          <Input type="number" step="any" placeholder="Longitude" value={form.longitude ?? ""} onChange={(e) => setForm({...form, longitude: e.target.value})}
                 data-testid="prop-lng" className="rounded-none border-zinc-400" />
        </div>
        <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}
                  data-testid="prop-desc" rows={3} className="w-full border border-zinc-400 rounded-none p-2 text-sm" />
        <div>
          <div className="label-overline text-zinc-600 mb-1">Amenities</div>
          <div className="flex flex-wrap gap-2" data-testid="prop-amenities">
            {AMENITIES.map((a) => (
              <label key={a} className={`text-xs border px-2.5 py-1 cursor-pointer ${form.amenities.includes(a) ? "bg-black text-white border-black" : "border-zinc-400"}`}>
                <input type="checkbox" checked={form.amenities.includes(a)} onChange={() => toggleAmenity(a)} className="hidden" /> {a}
              </label>
            ))}
          </div>
        </div>
        <Button type="submit" disabled={busy} data-testid="prop-create"
                className="rounded-none bg-blue-600 hover:bg-blue-700 text-white font-mono uppercase tracking-widest text-xs">
          Add Property
        </Button>
      </form>

      <div className="space-y-4">
        {items.length === 0 && <div className="text-sm text-zinc-500">No properties yet.</div>}
        {items.map((p) => (
          <ItemRow key={p.id} item={p}
                   testIdPrefix={`prop-${p.id}`}
                   subtitle={`${money(p.price)} · ${p.location || "—"} · ${p.area_sqft ? `${p.area_sqft} sq.ft` : ""} · ${p.status}`}
                   onDelete={() => remove(p.id)}
                   onUpload={(f) => uploadPhoto(p.id, f)} />
        ))}
      </div>
    </div>
  );
}

// ---------- INQUIRIES ----------
function InquiriesAdmin() {
  const [items, setItems] = useState([]);
  useEffect(() => { http.get("/marketing/builders/inquiries").then(r => setItems(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-3" data-testid="inquiries-list">
      {items.length === 0 && <div className="text-sm text-zinc-500">No inquiries yet.</div>}
      {items.map((i) => (
        <div key={i.id} className="border border-zinc-300 p-4" data-testid={`inq-${i.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="font-display font-black text-lg">{i.company_name}</div>
              <div className="text-sm text-zinc-700">{i.contact_person} · {i.city} · {i.years_in_business} yrs</div>
              <div className="text-xs text-zinc-500 mt-0.5">{i.email} · {i.phone} {i.gst_cin && `· GST: ${i.gst_cin}`}</div>
              <p className="text-sm text-zinc-700 mt-2 whitespace-pre-wrap">{i.pitch}</p>
            </div>
            <div className="text-xs text-zinc-500 font-mono shrink-0">{i.created_at?.slice(0, 10)}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Shared row with photo grid + upload + inline edit
function ItemRow({ item, subtitle, onDelete, onUpload, testIdPrefix, editFields }) {
  const ref = useRef();
  const [editing, setEditing] = useState(false);
  return (
    <div className="border border-zinc-300 p-4 flex flex-col gap-4" data-testid={testIdPrefix}>
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="font-display font-black text-lg">{item.title}</div>
          <div className="text-sm text-zinc-600 mt-0.5">{subtitle}</div>
          <div className="mt-2 flex gap-1.5 flex-wrap">
            {(item.photos || []).map((u, i) => (
              <img key={`${u}-${i}`} src={u} className="w-16 h-16 object-cover border border-zinc-300" alt="" />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <input ref={ref} type="file" accept="image/*" hidden
                 data-testid={`${testIdPrefix}-file`}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) { onUpload(f); e.target.value = ""; } }} />
          <button onClick={() => ref.current?.click()}
                  data-testid={`${testIdPrefix}-upload`}
                  className="label-overline px-3 py-1.5 border border-blue-500 text-blue-700 hover:bg-blue-600 hover:text-white">
            Add Photo
          </button>
          {editFields && (
            <button onClick={() => setEditing((v) => !v)}
                    data-testid={`${testIdPrefix}-edit`}
                    className="label-overline px-3 py-1.5 border border-zinc-500 text-zinc-700 hover:bg-zinc-900 hover:text-white">
              {editing ? "Close" : "Edit"}
            </button>
          )}
          <button onClick={onDelete}
                  data-testid={`${testIdPrefix}-delete`}
                  className="label-overline px-3 py-1.5 border border-red-400 text-red-600 hover:bg-red-600 hover:text-white">
            Delete
          </button>
        </div>
      </div>
      {editing && editFields && (
        <div className="border-t border-zinc-200 pt-4">{editFields}</div>
      )}
    </div>
  );
}

// Project inline editor — category, title, location, description, featured
function ProjectEdit({ item, onSaved }) {
  const [form, setForm] = useState({
    title: item.title || "",
    category: item.category || "Residential",
    location: item.location || "",
    description: item.description || "",
    featured: !!item.featured,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/marketing/projects/${item.id}`, form);
      toast.success("Project updated");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
             data-testid={`edit-${item.id}-title`}
             placeholder="Title" className="rounded-none border-zinc-400" />
      <select value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}
              data-testid={`edit-${item.id}-cat`}
              className="h-10 px-3 border border-zinc-400 rounded-none text-sm">
        {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
      </select>
      <Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})}
             placeholder="Location" className="rounded-none border-zinc-400" />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.featured}
               onChange={(e) => setForm({...form, featured: e.target.checked})} /> Featured
      </label>
      <textarea value={form.description} rows={2}
                onChange={(e) => setForm({...form, description: e.target.value})}
                className="md:col-span-2 border border-zinc-400 rounded-none p-2 text-sm" />
      <Button onClick={save} disabled={busy} data-testid={`edit-${item.id}-save`}
              className="md:col-span-2 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
        {busy ? "Saving…" : "Save changes"}
      </Button>
    </div>
  );
}

// Property inline editor — includes amenities checkbox list
const AMENITIES_LIST = [
  "Terrace Garden", "Covered Parking", "24/7 Security", "Power Backup",
  "Clubhouse", "Gym", "Swimming Pool", "Kids Play Area", "Elevator", "Vaastu Compliant",
];

function PropertyEdit({ item, onSaved }) {
  const [form, setForm] = useState({
    title: item.title || "",
    price: item.price || 0,
    location: item.location || "",
    area_sqft: item.area_sqft || 0,
    description: item.description || "",
    contact: item.contact || "",
    amenities: [...(item.amenities || [])],
    latitude: item.latitude ?? null,
    longitude: item.longitude ?? null,
    status: item.status || "available",
  });
  const [busy, setBusy] = useState(false);
  const toggle = (a) => setForm((f) =>
    f.amenities.includes(a) ? {...f, amenities: f.amenities.filter((x) => x !== a)}
                             : {...f, amenities: [...f.amenities, a]}
  );
  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/marketing/properties/${item.id}`, form);
      toast.success("Property updated");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})}
             placeholder="Title" className="rounded-none border-zinc-400" />
      <Input type="number" value={form.price} onChange={(e) => setForm({...form, price: Number(e.target.value)})}
             placeholder="Price (₹)" className="rounded-none border-zinc-400" />
      <Input value={form.location} onChange={(e) => setForm({...form, location: e.target.value})}
             placeholder="Location" className="rounded-none border-zinc-400" />
      <Input type="number" value={form.area_sqft} onChange={(e) => setForm({...form, area_sqft: Number(e.target.value)})}
             placeholder="Area (sqft)" className="rounded-none border-zinc-400" />
      <Input value={form.contact} onChange={(e) => setForm({...form, contact: e.target.value})}
             placeholder="Contact" className="rounded-none border-zinc-400" />
      <select value={form.status} onChange={(e) => setForm({...form, status: e.target.value})}
              className="h-10 px-3 border border-zinc-400 rounded-none text-sm">
        <option value="available">Available</option>
        <option value="sold">Sold</option>
        <option value="reserved">Reserved</option>
      </select>
      <Input type="number" step="0.0001" value={form.latitude ?? ""}
             onChange={(e) => setForm({...form, latitude: e.target.value ? Number(e.target.value) : null})}
             placeholder="Latitude" className="rounded-none border-zinc-400" />
      <Input type="number" step="0.0001" value={form.longitude ?? ""}
             onChange={(e) => setForm({...form, longitude: e.target.value ? Number(e.target.value) : null})}
             placeholder="Longitude" className="rounded-none border-zinc-400" />
      <textarea value={form.description} rows={2}
                onChange={(e) => setForm({...form, description: e.target.value})}
                className="md:col-span-2 border border-zinc-400 rounded-none p-2 text-sm"
                placeholder="Description" />
      <div className="md:col-span-2">
        <div className="label-overline text-zinc-600 mb-2">Amenities (editable)</div>
        <div className="flex flex-wrap gap-2" data-testid={`edit-${item.id}-amenities`}>
          {AMENITIES_LIST.map((a) => (
            <label key={a} className={`text-xs border px-2.5 py-1 cursor-pointer ${form.amenities.includes(a) ? "bg-black text-white border-black" : "border-zinc-400"}`}>
              <input type="checkbox" checked={form.amenities.includes(a)}
                     onChange={() => toggle(a)} className="hidden" /> {a}
            </label>
          ))}
        </div>
      </div>
      <Button onClick={save} disabled={busy} data-testid={`edit-${item.id}-save`}
              className="md:col-span-2 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
        {busy ? "Saving…" : "Save changes"}
      </Button>
    </div>
  );
}


// ---------- BLOG ----------
function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", slug: "", excerpt: "", body: "",
                                     cover_image_url: "", author: "Studio DHP", tag: "", published: true });

  const load = () => http.get("/marketing/admin/blog").then((r) => setPosts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim() || !form.body.trim()) { toast.error("Title and body are required"); return; }
    try {
      await http.post("/marketing/blog", form);
      toast.success("Post created");
      setForm({ title: "", slug: "", excerpt: "", body: "", cover_image_url: "", author: "Studio DHP", tag: "", published: true });
      setCreating(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try { await http.delete(`/marketing/blog/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadCover = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await http.post(`/marketing/blog/${id}/cover`, fd); toast.success("Cover updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="blog-admin">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-black">Blog Posts</h2>
        <Button onClick={() => setCreating((v) => !v)} data-testid="new-blog-btn"
                className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
          {creating ? "Cancel" : "+ New Post"}
        </Button>
      </div>

      {creating && (
        <div className="border-2 border-black p-5 grid md:grid-cols-2 gap-3" data-testid="new-blog-form">
          <Input placeholder="Title *" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="rounded-none border-zinc-400" />
          <Input placeholder="Slug (auto if blank)" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} className="rounded-none border-zinc-400" />
          <Input placeholder="Author" value={form.author} onChange={(e) => setForm({...form, author: e.target.value})} className="rounded-none border-zinc-400" />
          <Input placeholder="Tag (e.g. Design Notes)" value={form.tag} onChange={(e) => setForm({...form, tag: e.target.value})} className="rounded-none border-zinc-400" />
          <textarea placeholder="Excerpt (short summary)" rows={2} value={form.excerpt}
                    onChange={(e) => setForm({...form, excerpt: e.target.value})}
                    className="md:col-span-2 border border-zinc-400 rounded-none p-2 text-sm" />
          <textarea placeholder="Body (HTML supported)" rows={8} value={form.body}
                    onChange={(e) => setForm({...form, body: e.target.value})}
                    className="md:col-span-2 border border-zinc-400 rounded-none p-2 text-sm font-mono" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.published}
                   onChange={(e) => setForm({...form, published: e.target.checked})} /> Published
          </label>
          <Button onClick={create} data-testid="new-blog-submit"
                  className="rounded-none bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs">
            Create Post
          </Button>
        </div>
      )}

      <div className="grid gap-3">
        {posts.length === 0 && !creating && (
          <div className="text-sm text-zinc-500 border border-dashed border-zinc-400 p-8 text-center">No blog posts yet.</div>
        )}
        {posts.map((p) => (
          <BlogRow key={p.id} p={p} onDelete={() => remove(p.id)}
                   onUpload={(f) => uploadCover(p.id, f)} onSaved={load} />
        ))}
      </div>
    </div>
  );
}

function BlogRow({ p, onDelete, onUpload, onSaved }) {
  const ref = useRef();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    title: p.title, slug: p.slug, excerpt: p.excerpt || "", body: p.body || "",
    author: p.author || "Studio DHP", tag: p.tag || "", published: !!p.published,
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/marketing/blog/${p.id}`, form);
      toast.success("Post updated");
      setEditing(false);
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-zinc-300 p-4 flex flex-col gap-4" data-testid={`blog-row-${p.id}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        {p.cover_image_url && (
          <img src={p.cover_image_url} className="w-32 h-24 object-cover border border-zinc-300" alt="" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <div className="font-display font-black text-lg">{p.title}</div>
            {!p.published && <span className="text-[10px] px-1.5 border border-zinc-400 uppercase tracking-widest">Draft</span>}
          </div>
          <div className="text-xs text-zinc-600 mt-0.5">
            /blog/{p.slug} · {p.tag || "—"} · {p.author}
          </div>
          {p.excerpt && <div className="text-sm text-zinc-700 mt-2 line-clamp-2">{p.excerpt}</div>}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <input ref={ref} type="file" accept="image/*" hidden
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) { onUpload(f); e.target.value = ""; } }} />
          <button onClick={() => ref.current?.click()}
                  className="label-overline px-3 py-1.5 border border-blue-500 text-blue-700 hover:bg-blue-600 hover:text-white">
            Cover
          </button>
          <button onClick={() => setEditing((v) => !v)} data-testid={`blog-${p.id}-edit`}
                  className="label-overline px-3 py-1.5 border border-zinc-500 text-zinc-700 hover:bg-zinc-900 hover:text-white">
            {editing ? "Close" : "Edit"}
          </button>
          <button onClick={onDelete} data-testid={`blog-${p.id}-delete`}
                  className="label-overline px-3 py-1.5 border border-red-400 text-red-600 hover:bg-red-600 hover:text-white">
            Delete
          </button>
        </div>
      </div>

      {editing && (
        <div className="border-t border-zinc-200 pt-4 grid md:grid-cols-2 gap-3">
          <Input placeholder="Title" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} className="rounded-none border-zinc-400" />
          <Input placeholder="Slug" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} className="rounded-none border-zinc-400" />
          <Input placeholder="Author" value={form.author} onChange={(e) => setForm({...form, author: e.target.value})} className="rounded-none border-zinc-400" />
          <Input placeholder="Tag" value={form.tag} onChange={(e) => setForm({...form, tag: e.target.value})} className="rounded-none border-zinc-400" />
          <textarea rows={2} value={form.excerpt} onChange={(e) => setForm({...form, excerpt: e.target.value})}
                    className="md:col-span-2 border border-zinc-400 rounded-none p-2 text-sm" placeholder="Excerpt" />
          <textarea rows={10} value={form.body} onChange={(e) => setForm({...form, body: e.target.value})}
                    className="md:col-span-2 border border-zinc-400 rounded-none p-2 text-sm font-mono" placeholder="Body (HTML)" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.published}
                   onChange={(e) => setForm({...form, published: e.target.checked})} /> Published
          </label>
          <Button onClick={save} disabled={busy} data-testid={`blog-${p.id}-save`}
                  className="md:col-span-2 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      )}
    </div>
  );
}



// ---------- LOGIN SLIDER (5 photos on /login) ----------
function LoginSliderAdmin() {
  const [photos, setPhotos] = useState(["", "", "", "", ""]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/marketing/login-slider").then(({ data }) => {
      const arr = (data?.photos || []).slice(0, 5);
      while (arr.length < 5) arr.push("");
      setPhotos(arr);
    }).catch(() => {});
  }, []);

  const upload = async (i, file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await http.post("/marketing/login-slider/upload", fd);
      const next = [...photos]; next[i] = data.url;
      setPhotos(next);
      toast.success(`Slot ${i + 1} uploaded`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await http.put("/marketing/login-slider", { photos: photos.filter(Boolean) });
      toast.success("Login slider saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const clear = (i) => {
    const next = [...photos]; next[i] = ""; setPhotos(next);
  };

  return (
    <div className="space-y-6" data-testid="login-slider-admin">
      <div>
        <h2 className="font-display text-2xl font-black">Login Page Slider</h2>
        <p className="text-sm text-zinc-600 mt-1">
          Upload up to 5 photos. They rotate on the Studio Login page every 5 seconds.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {photos.map((url, i) => (
          <SlotCard key={`slot-${i}`} idx={i} url={url}
                    onUpload={(f) => upload(i, f)}
                    onClear={() => clear(i)}
                    onUrl={(v) => { const next = [...photos]; next[i] = v; setPhotos(next); }} />
        ))}
      </div>
      <Button onClick={save} disabled={busy} data-testid="login-slider-save"
              className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
        {busy ? "Saving…" : "Save Slider"}
      </Button>
    </div>
  );
}

function SlotCard({ idx, url, onUpload, onClear, onUrl }) {
  const ref = useRef();
  return (
    <div className="border border-zinc-300 p-3" data-testid={`slider-slot-${idx}`}>
      <div className="label-overline text-zinc-600 mb-2">Slot {idx + 1}</div>
      <div className="aspect-[4/3] bg-zinc-100 border border-zinc-200 overflow-hidden mb-3 flex items-center justify-center">
        {url ? (
          <img src={url} alt="" className="w-full h-full object-cover" />
        ) : (
          <span className="text-xs text-zinc-500">Empty</span>
        )}
      </div>
      <input ref={ref} type="file" accept="image/*" hidden
             data-testid={`slider-slot-${idx}-file`}
             onChange={(e) => { const f = e.target.files?.[0]; if (f) { onUpload(f); e.target.value = ""; } }} />
      <div className="flex gap-2">
        <button onClick={() => ref.current?.click()}
                data-testid={`slider-slot-${idx}-upload`}
                className="flex-1 label-overline px-2 py-1.5 border border-blue-500 text-blue-700 hover:bg-blue-600 hover:text-white">
          Upload
        </button>
        {url && (
          <button onClick={onClear}
                  data-testid={`slider-slot-${idx}-clear`}
                  className="label-overline px-2 py-1.5 border border-red-400 text-red-600 hover:bg-red-600 hover:text-white">
            Clear
          </button>
        )}
      </div>
      <Input placeholder="Or paste URL" value={url}
             onChange={(e) => onUrl(e.target.value)}
             className="rounded-none border-zinc-400 mt-2 text-xs" />
    </div>
  );
}



// ---------- TEAM (About Us team profiles) ----------
function TeamAdmin() {
  const [members, setMembers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", bio: "", photo_url: "", order: 0 });

  const load = () => http.get("/marketing/admin/team").then((r) => setMembers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    try {
      await http.post("/marketing/team", { ...form, order: Number(form.order) || 0 });
      toast.success("Team member added");
      setForm({ name: "", role: "", bio: "", photo_url: "", order: 0 });
      setCreating(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this team member?")) return;
    try { await http.delete(`/marketing/team/${id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadPhoto = async (id, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { await http.post(`/marketing/team/${id}/photo`, fd); toast.success("Photo updated"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="team-admin">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-black">About Us — Team Members</h2>
        <Button onClick={() => setCreating((v) => !v)} data-testid="new-team-btn"
                className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
          {creating ? "Cancel" : "+ New Member"}
        </Button>
      </div>

      {creating && (
        <div className="border-2 border-black p-5 grid md:grid-cols-2 gap-3" data-testid="new-team-form">
          <Input placeholder="Name *" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-none border-zinc-400" />
          <Input placeholder="Designation (e.g. Principal Architect)" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="rounded-none border-zinc-400" />
          <Input placeholder="Photo URL (paste, or upload after creating)" value={form.photo_url} onChange={(e) => setForm({...form, photo_url: e.target.value})} className="rounded-none border-zinc-400" />
          <Input type="number" placeholder="Sort order" value={form.order} onChange={(e) => setForm({...form, order: e.target.value})} className="rounded-none border-zinc-400" />
          <textarea placeholder="Bio (short)" rows={2} value={form.bio}
                    onChange={(e) => setForm({...form, bio: e.target.value})}
                    className="md:col-span-2 border border-zinc-400 rounded-none p-2 text-sm" />
          <Button onClick={create} data-testid="new-team-submit"
                  className="rounded-none bg-orange-600 hover:bg-orange-700 text-white font-mono uppercase tracking-widest text-xs">
            Add Member
          </Button>
        </div>
      )}

      <div className="grid gap-3">
        {members.length === 0 && !creating && (
          <div className="text-sm text-zinc-500 border border-dashed border-zinc-400 p-8 text-center">No team members yet.</div>
        )}
        {members.map((m) => (
          <TeamRow key={m.id} m={m} onDelete={() => remove(m.id)}
                   onUpload={(f) => uploadPhoto(m.id, f)} onSaved={load} />
        ))}
      </div>
    </div>
  );
}

function TeamRow({ m, onDelete, onUpload, onSaved }) {
  const ref = useRef();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: m.name, role: m.role, bio: m.bio || "", photo_url: m.photo_url || "", order: m.order || 0 });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/marketing/team/${m.id}`, { ...form, order: Number(form.order) || 0 });
      toast.success("Team member updated"); setEditing(false); onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-zinc-300 p-4 flex flex-col gap-4" data-testid={`team-row-${m.id}`}>
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        {m.photo_url ? (
          <img src={m.photo_url} alt="" className="w-24 h-32 object-cover border border-zinc-300" />
        ) : (
          <div className="w-24 h-32 border border-dashed border-zinc-400 flex items-center justify-center text-xs text-zinc-500">No photo</div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-display font-black text-lg">{m.name}</div>
          <div className="text-sm text-zinc-600 mt-0.5">{m.role || "—"}</div>
          {m.bio && <div className="text-sm text-zinc-700 mt-2 line-clamp-2">{m.bio}</div>}
          <div className="text-xs text-zinc-500 mt-2">Sort order: {m.order || 0}</div>
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <input ref={ref} type="file" accept="image/*" hidden
                 data-testid={`team-${m.id}-file`}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) { onUpload(f); e.target.value = ""; } }} />
          <button onClick={() => ref.current?.click()} data-testid={`team-${m.id}-photo`}
                  className="label-overline px-3 py-1.5 border border-blue-500 text-blue-700 hover:bg-blue-600 hover:text-white">
            Photo
          </button>
          <button onClick={() => setEditing((v) => !v)} data-testid={`team-${m.id}-edit`}
                  className="label-overline px-3 py-1.5 border border-zinc-500 text-zinc-700 hover:bg-zinc-900 hover:text-white">
            {editing ? "Close" : "Edit"}
          </button>
          <button onClick={onDelete} data-testid={`team-${m.id}-delete`}
                  className="label-overline px-3 py-1.5 border border-red-400 text-red-600 hover:bg-red-600 hover:text-white">
            Delete
          </button>
        </div>
      </div>
      {editing && (
        <div className="border-t border-zinc-200 pt-4 grid md:grid-cols-2 gap-3">
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded-none border-zinc-400" />
          <Input placeholder="Designation" value={form.role} onChange={(e) => setForm({...form, role: e.target.value})} className="rounded-none border-zinc-400" />
          <Input placeholder="Photo URL" value={form.photo_url} onChange={(e) => setForm({...form, photo_url: e.target.value})} className="rounded-none border-zinc-400" />
          <Input type="number" placeholder="Sort order" value={form.order} onChange={(e) => setForm({...form, order: e.target.value})} className="rounded-none border-zinc-400" />
          <textarea rows={2} value={form.bio} onChange={(e) => setForm({...form, bio: e.target.value})}
                    className="md:col-span-2 border border-zinc-400 rounded-none p-2 text-sm" placeholder="Bio" />
          <Button onClick={save} disabled={busy} data-testid={`team-${m.id}-save`}
                  className="md:col-span-2 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </div>
      )}
    </div>
  );
}



// ---------- LANDING PAGE (numbers, expertise, CTA band) ----------
const DEFAULT_LANDING = {
  landing_numbers: { items: [
    { k: "Since", v: "2014", sub: "Practice established" },
    { k: "Turnkey", v: "100+", sub: "Projects delivered" },
    { k: "Cities", v: "12", sub: "Across India" },
    { k: "Studio", v: "18", sub: "Architects & engineers" },
  ]},
  landing_expertise: {
    title: "Three studios",
    title_italic: "— one philosophy.",
    intro: "Whether it's the raw civil frame, the interior architecture that softens it, or the complete turnkey handover — every stage is drawn, engineered, and installed by the same studio. No handoffs, no compromises.",
    cards: [
      { k: "01", t: "Civil & Structural", d: "Ground-up construction with in-house structural engineering, RCC design, and quality-controlled execution." },
      { k: "02", t: "Interior Architecture", d: "Bespoke interior design — modular kitchens, false ceilings, custom joinery, and lighting design." },
      { k: "03", t: "Turnkey Projects", d: "Concept to handover: architecture + structure + interiors + landscape, delivered as one contract." },
    ],
  },
  landing_cta: {
    overline: "Consultation",
    title: "Let's draw",
    title_italic: "yours.",
    copy: "A short conversation is usually all it takes to know if we're a fit. No commitment, no rush — just an honest look at what your project could be.",
    button_label: "Book a Design Session →",
    button_href: "/contact",
  },
};

function LandingAdmin() {
  const [state, setState] = useState(DEFAULT_LANDING);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    http.get("/marketing/landing").then(({ data }) => {
      setState({
        landing_numbers: data.landing_numbers || DEFAULT_LANDING.landing_numbers,
        landing_expertise: data.landing_expertise || DEFAULT_LANDING.landing_expertise,
        landing_cta: data.landing_cta || DEFAULT_LANDING.landing_cta,
      });
    }).catch(() => {});
  }, []);

  const saveKey = async (key) => {
    setBusy({ ...busy, [key]: true });
    try {
      await http.put(`/marketing/landing/${key}`, { value: state[key] });
      toast.success(`${key.replace("landing_", "")} saved`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy({ ...busy, [key]: false }); }
  };

  const setNumbers = (items) => setState({ ...state, landing_numbers: { items } });
  const setExpertise = (patch) => setState({ ...state, landing_expertise: { ...state.landing_expertise, ...patch } });
  const setCta = (patch) => setState({ ...state, landing_cta: { ...state.landing_cta, ...patch } });

  const nums = state.landing_numbers.items;
  const exp = state.landing_expertise;
  const cta = state.landing_cta;

  return (
    <div className="space-y-10" data-testid="landing-admin">
      {/* Numbers */}
      <section>
        <h2 className="font-display text-2xl font-black">Core Numbers Strip</h2>
        <p className="text-sm text-zinc-600 mt-1">The 4-column stats bar just below the hero.</p>
        <div className="grid md:grid-cols-4 gap-3 mt-4">
          {nums.map((n, i) => (
            <div key={`landing-num-${i}`} className="border border-zinc-300 p-3 space-y-2" data-testid={`landing-num-${i}`}>
              <Input placeholder="Label" value={n.k}
                     onChange={(e) => { const next=[...nums]; next[i]={...next[i], k:e.target.value}; setNumbers(next); }}
                     className="rounded-none border-zinc-400" />
              <Input placeholder="Value" value={n.v}
                     onChange={(e) => { const next=[...nums]; next[i]={...next[i], v:e.target.value}; setNumbers(next); }}
                     className="rounded-none border-zinc-400 font-bold" />
              <Input placeholder="Sub-caption" value={n.sub}
                     onChange={(e) => { const next=[...nums]; next[i]={...next[i], sub:e.target.value}; setNumbers(next); }}
                     className="rounded-none border-zinc-400" />
            </div>
          ))}
        </div>
        <Button onClick={() => saveKey("landing_numbers")} disabled={busy.landing_numbers}
                data-testid="save-numbers"
                className="mt-4 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
          {busy.landing_numbers ? "Saving…" : "Save Numbers"}
        </Button>
      </section>

      {/* Expertise */}
      <section>
        <h2 className="font-display text-2xl font-black">Expertise Teaser</h2>
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <Input placeholder="Heading (before break)" value={exp.title}
                 onChange={(e) => setExpertise({ title: e.target.value })}
                 className="rounded-none border-zinc-400" />
          <Input placeholder="Italic tail (gold)" value={exp.title_italic}
                 onChange={(e) => setExpertise({ title_italic: e.target.value })}
                 className="rounded-none border-zinc-400" />
          <textarea placeholder="Intro paragraph" rows={3} value={exp.intro}
                    onChange={(e) => setExpertise({ intro: e.target.value })}
                    className="md:col-span-2 border border-zinc-400 rounded-none p-2 text-sm" />
        </div>
        <div className="grid md:grid-cols-3 gap-3 mt-4">
          {(exp.cards || []).map((c, i) => (
            <div key={`landing-exp-${i}`} className="border border-zinc-300 p-3 space-y-2" data-testid={`landing-exp-${i}`}>
              <Input placeholder="Number" value={c.k}
                     onChange={(e) => { const next=[...exp.cards]; next[i]={...next[i], k:e.target.value}; setExpertise({ cards: next }); }}
                     className="rounded-none border-zinc-400" />
              <Input placeholder="Title" value={c.t}
                     onChange={(e) => { const next=[...exp.cards]; next[i]={...next[i], t:e.target.value}; setExpertise({ cards: next }); }}
                     className="rounded-none border-zinc-400" />
              <textarea placeholder="Description" rows={3} value={c.d}
                        onChange={(e) => { const next=[...exp.cards]; next[i]={...next[i], d:e.target.value}; setExpertise({ cards: next }); }}
                        className="w-full border border-zinc-400 rounded-none p-2 text-sm" />
            </div>
          ))}
        </div>
        <Button onClick={() => saveKey("landing_expertise")} disabled={busy.landing_expertise}
                data-testid="save-expertise"
                className="mt-4 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
          {busy.landing_expertise ? "Saving…" : "Save Expertise"}
        </Button>
      </section>

      {/* CTA */}
      <section>
        <h2 className="font-display text-2xl font-black">Consultation CTA (bottom band)</h2>
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          <Input placeholder="Overline (small caps)" value={cta.overline}
                 onChange={(e) => setCta({ overline: e.target.value })}
                 className="rounded-none border-zinc-400" />
          <Input placeholder="Heading" value={cta.title}
                 onChange={(e) => setCta({ title: e.target.value })}
                 className="rounded-none border-zinc-400" />
          <Input placeholder="Italic tail (gold)" value={cta.title_italic}
                 onChange={(e) => setCta({ title_italic: e.target.value })}
                 className="rounded-none border-zinc-400" />
          <Input placeholder="Button label" value={cta.button_label}
                 onChange={(e) => setCta({ button_label: e.target.value })}
                 className="rounded-none border-zinc-400" />
          <Input placeholder="Button link (/contact)" value={cta.button_href}
                 onChange={(e) => setCta({ button_href: e.target.value })}
                 className="rounded-none border-zinc-400" />
          <textarea placeholder="Body copy" rows={3} value={cta.copy}
                    onChange={(e) => setCta({ copy: e.target.value })}
                    className="md:col-span-2 border border-zinc-400 rounded-none p-2 text-sm" />
        </div>
        <Button onClick={() => saveKey("landing_cta")} disabled={busy.landing_cta}
                data-testid="save-cta"
                className="mt-4 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
          {busy.landing_cta ? "Saving…" : "Save CTA Band"}
        </Button>
      </section>
    </div>
  );
}



// ---------- BUILDERS CORNER ----------
function BuildersAdmin() {
  const [data, setData] = useState({
    hero_left_url: "", hero_right_url: "",
    stats: [{k:"",l:""},{k:"",l:""},{k:"",l:""},{k:"",l:""}],
    testimonial: { body: "", who: "", role: "" },
  });
  const [busy, setBusy] = useState(false);
  const leftRef = useRef();
  const rightRef = useRef();

  const load = () => http.get("/marketing/builders").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (slot, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      await http.post(`/marketing/builders/hero/${slot}`, fd);
      toast.success(`${slot === "left" ? "Left" : "Right"} hero image updated`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { e.target.value = ""; }
  };

  const setStat = (i, k, v) => setData((d) => {
    const stats = [...d.stats];
    stats[i] = { ...stats[i], [k]: v };
    return { ...d, stats };
  });

  const setTst = (k, v) => setData((d) => ({ ...d, testimonial: { ...d.testimonial, [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      await http.patch("/marketing/builders", {
        stats: data.stats,
        testimonial: data.testimonial,
      });
      toast.success("Builders page updated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const renderHero = (slot, url, ref) => (
    <div className="border border-zinc-300 p-4 space-y-3" data-testid={`builders-hero-${slot}`}>
      <div className="label-overline text-zinc-500">Hero — {slot === "left" ? "Left" : "Right"} panel</div>
      <div className="aspect-[4/3] bg-zinc-100 overflow-hidden">
        {url
          ? <img src={url} alt="" className="w-full h-full object-cover"
                 data-testid={`builders-hero-${slot}-img`} />
          : <div className="w-full h-full flex items-center justify-center text-xs text-zinc-400 uppercase tracking-widest">Not set — default Unsplash still is shown</div>}
      </div>
      <input type="file" hidden accept="image/*" ref={ref}
             data-testid={`builders-hero-${slot}-file`}
             onChange={(e) => upload(slot, e)} />
      <Button onClick={() => ref.current?.click()}
              data-testid={`builders-hero-${slot}-upload`}
              className="w-full rounded-none bg-black hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
        Upload {slot === "left" ? "Left" : "Right"} Image
      </Button>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="builders-admin">
      <section>
        <h2 className="font-display text-2xl font-black">Hero Images</h2>
        <p className="text-sm text-zinc-500 mt-1">Two full-bleed photos shown side-by-side on the Builders Corner page.</p>
        <div className="grid md:grid-cols-2 gap-4 mt-4">
          {renderHero("left",  data.hero_left_url,  leftRef)}
          {renderHero("right", data.hero_right_url, rightRef)}
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl font-black">Stats Rail (4 tiles)</h2>
        <p className="text-sm text-zinc-500 mt-1">Big number + short caption for each — shown as an editorial band below the hero.</p>
        <div className="grid md:grid-cols-2 gap-3 mt-4">
          {data.stats.map((s, i) => (
            <div key={`stat-${i}`} className="border border-zinc-300 p-3 space-y-2" data-testid={`builders-stat-${i}`}>
              <div className="label-overline text-zinc-500">Tile {i + 1}</div>
              <Input value={s.k} onChange={(e) => setStat(i, "k", e.target.value)}
                     data-testid={`builders-stat-${i}-k`}
                     placeholder="10+" className="rounded-none border-zinc-400" />
              <Input value={s.l} onChange={(e) => setStat(i, "l", e.target.value)}
                     data-testid={`builders-stat-${i}-l`}
                     placeholder="Years partnering with builders" className="rounded-none border-zinc-400" />
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display text-2xl font-black">Testimonial</h2>
        <p className="text-sm text-zinc-500 mt-1">A single builder quote centred above the callback form.</p>
        <div className="grid md:grid-cols-[2fr_1fr_1fr] gap-3 mt-4">
          <div>
            <div className="label-overline text-zinc-500 mb-1">Quote body</div>
            <textarea rows={4} value={data.testimonial.body}
                      onChange={(e) => setTst("body", e.target.value)}
                      data-testid="builders-tst-body"
                      className="w-full rounded-none border border-zinc-400 p-3 font-serif" />
          </div>
          <div>
            <div className="label-overline text-zinc-500 mb-1">Attributed to (name)</div>
            <Input value={data.testimonial.who}
                   onChange={(e) => setTst("who", e.target.value)}
                   data-testid="builders-tst-who"
                   className="rounded-none border-zinc-400" />
          </div>
          <div>
            <div className="label-overline text-zinc-500 mb-1">Role / Company</div>
            <Input value={data.testimonial.role}
                   onChange={(e) => setTst("role", e.target.value)}
                   data-testid="builders-tst-role"
                   className="rounded-none border-zinc-400" />
          </div>
        </div>
      </section>

      <Button onClick={save} disabled={busy}
              data-testid="builders-save"
              className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">
        {busy ? "Saving…" : "Save Builders Page"}
      </Button>
    </div>
  );
}
